import { ApplicantNameCell } from './ApplicantNameCell';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { No, StrongNo, StrongYes, Yes } from '@/assets/icons/scorecard';
import { secondsToHumanTime } from '@/utils/secondsToHumanTime';
import { KImage, KText } from '@/ui-library/components';
import { TIME_ZONE_CONSTANTS } from '@/utils/timeZoneConstants';
import { Flex, Icon, Link } from '@chakra-ui/react';
import moment from 'moment-timezone';

const interviewTypeValueMap = {
  one_on_one: '1 on 1',
  panel: 'Panel',
};

const interviewLocationValueMap = {
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  phone_interview: 'Phone Interview',
  onsite: 'Onsite',
  teams: 'Teams',
  hackerrank: 'HackerRank',
};

export const NameCell = (row) => {
  const { ats_interview = {} } = row?.original?.interview || {};
  const { name, id } = ats_interview || {};

  return (
    <Link href={`/interviews/${id}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        _hover={{
          textDecoration: 'underline',
          color: 'primary-500',
          cursor: 'pointer',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

export const InterviewerName = (row) => {
  const { attendee = {} } = row?.original?.ats_interview_attendee || {};
  const { name, avatar } = attendee || {};

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
            {name || '-'}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

export const ApplicantName = (row) => {
  const atsApplication = row?.original?.ats_application;
  const { id: jobId } = row?.original?.job?.ats_job || {};

  return <ApplicantNameCell atsApplication={atsApplication} jobId={jobId} />;
};

export const ScorecardResult = (row) => {
  const { scorecard_Rating = '' } = row?.original || {};
  const labelMap = {
    'Strong yes': {
      label: 'Strong Yes',
      color: 'pastel-2-dark',
      bg: 'pastel-2-light',
      icon: StrongYes,
    },
    'Strong no': {
      label: 'Strong No',
      color: 'pastel-1-dark',
      bg: 'pastel-1-light',
      icon: StrongNo,
    },
    No: {
      label: 'No',
      color: 'pastel-1-dark',
      bg: 'pastel-1-light',
      icon: No,
    },
    Yes: {
      label: 'Yes',
      color: 'pastel-2-dark',
      bg: 'pastel-2-light',
      icon: Yes,
    },
    Pending: {
      label: 'Pending',
      color: '#D98647',
      bg: '#FFF0E0',
    },
  };

  const { label, color, bg, icon } = labelMap[scorecard_Rating] || {};

  return (
    <Flex
      gap="6px"
      p="0.125em 0.5em"
      borderRadius="sm"
      bg={bg}
      borderColor="transparent"
      alignItems="center"
    >
      {icon && <Icon h="24px" w="24px" color={color} as={icon}></Icon>}
      <KText color={color} size="sm-medium">
        {label || '-'}
      </KText>
    </Flex>
  );
};

export const Jobname = (row) => {
  const { ats_job = {} } = row?.original?.job || {};
  const { name, id: jobId } = ats_job || {};
  return (
    <Link href={`/jobs/${jobId}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        _hover={{
          textDecoration: 'underline',
          color: 'primary-500',
          cursor: 'pointer',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

export const TimeToFillScorecard = (row) => {
  const duration = row?.original?.time_to_fill_scorecard || 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};

export const TimeSpentInterviewing = (row) => {
  const duration = row?.original?.interview_duration || 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};

export const InterviewType = (row) => {
  const { interview_type = '' } = row?.original || {};
  const label = interviewTypeValueMap[interview_type] ?? interview_type;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {label || '-'}
    </KText>
  );
};

const formatInterviewTimeOnly = ({
  interviewTime,
  timeFormat = 'hh:mm A',
  dateFormat = 'DD-MM-YYYY',
  currUserTimezone,
}) => {
  if (!interviewTime || !currUserTimezone) return null;
  const m = moment(interviewTime).tz(currUserTimezone);
  if (!m.isValid()) return null;
  const dateLabel = m.format(dateFormat);
  const timeLabel = m.format(timeFormat);
  const abbr = TIME_ZONE_CONSTANTS[currUserTimezone]?.abbreviation || 'UTC';
  return `${dateLabel} ${timeLabel} ${abbr}`;
};

export const InterviewTime = ({
  row,
  dateFormat,
  timeFormat,
  currUserTimezone,
}) => {
  const { interview_time } = row?.original || {};
  const formatted = formatInterviewTimeOnly({
    interviewTime: interview_time,
    timeFormat: timeFormat,
    dateFormat: dateFormat?.value ?? 'DD-MM-YYYY',
    currUserTimezone: currUserTimezone,
  });

  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {formatted ?? '-'}
    </KText>
  );
};

export const InterviewLocation = (row) => {
  const { interview_location_type = '' } = row?.original || {};
  const label =
    interviewLocationValueMap[interview_location_type] ??
    interview_location_type;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {label || '-'}
    </KText>
  );
};

export const StageName = (row) => {
  const { stage_name = '' } = row?.original || {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {stage_name || '-'}
    </KText>
  );
};

export const DepartmentName = (row) => {
  const { department_name = '' } = row?.original || {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {department_name || '-'}
    </KText>
  );
};

const parseOfficeNames = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return value.trim() ? [value.trim()] : [];
    }
  }
  return [];
};

const renderOfficeInitial = (items: string[]) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((name, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {name}
      </KText>
    ))}
  </Flex>
);

const renderOfficeOthers = (list: string[]) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((name, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KText
          fontSize="sm"
          color="secondary-700"
          isTruncated
          enableTooltip={true}
        >
          {name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

export const OfficeNames = (row) => {
  const { office_names } = row?.original || {};
  const offices = parseOfficeNames(office_names);

  return (
    <Flex w="100%">
      <ViewMorePopover
        list={offices}
        max={1}
        type="officeList"
        renderInitial={renderOfficeInitial}
        renderOthers={renderOfficeOthers}
        emptyMsg="-"
        trigger="hover"
      />
    </Flex>
  );
};

export const ScheduledBy = (row) => {
  const { scheduledBy } = row?.original || {};
  const name =
    scheduledBy?.name ?? (typeof scheduledBy === 'string' ? scheduledBy : '');
  const avatar = scheduledBy?.avatar;

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
            {name}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

export const CancelledBy = (row) => {
  const { cancelledBy } = row?.original || {};
  const name =
    cancelledBy?.name ?? (typeof cancelledBy === 'string' ? cancelledBy : '');
  const avatar = cancelledBy?.avatar;

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
            {name}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};
